import { createSlice } from "@reduxjs/toolkit";
import {
  getIntegrationStatus,
  initiateIntegration,
  disconnectIntegration,
  toggleInventorySync,
  syncInventoryNow,
} from "./integrationAction";

interface IntegrationState {
  connected: boolean;
  connectedAt: string | null;
  provider: string;
  inventorySyncEnabled: boolean;
  lastSyncAt: string | null;
  loading: boolean;
  initiating: boolean;
  disconnecting: boolean;
  togglingSync: boolean;
  syncing: boolean;
  syncResult: { synced: number; errors: number; total: number } | null;
  amazonTokenRevoked: boolean;
  reauthUrl: string | null;
  error: string | null;
}

const initialState: IntegrationState = {
  connected: false,
  connectedAt: null,
  provider: "caleffi_pricing",
  inventorySyncEnabled: false,
  lastSyncAt: null,
  loading: false,
  initiating: false,
  disconnecting: false,
  togglingSync: false,
  syncing: false,
  syncResult: null,
  error: null,
  amazonTokenRevoked: false,
  reauthUrl: null,
};

const integrationSlice = createSlice({
  name: "integration",
  initialState,
  reducers: {
    clearIntegrationError: (state) => {
      state.error = null;
    },
    clearSyncResult: (state) => {
      state.syncResult = null;
    },
  },
  extraReducers: (builder) => {
    // --- getIntegrationStatus ---
    builder
      .addCase(getIntegrationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getIntegrationStatus.fulfilled, (state, action: any) => {
        state.loading = false;
        state.connected = action.payload?.connected ?? false;
        state.connectedAt = action.payload?.connectedAt ?? null;
        state.inventorySyncEnabled = action.payload?.inventorySyncEnabled ?? false;
        state.lastSyncAt = action.payload?.lastSyncAt ?? null;
        state.amazonTokenRevoked = action.payload?.amazonTokenRevoked ?? false;
        state.reauthUrl = action.payload?.reauthUrl ?? null;
      })
      .addCase(getIntegrationStatus.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload ?? "Errore nel caricamento dello stato";
      });

    // --- initiateIntegration ---
    builder
      .addCase(initiateIntegration.pending, (state) => {
        state.initiating = true;
        state.error = null;
      })
      .addCase(initiateIntegration.fulfilled, (state) => {
        state.initiating = false;
        // Redirect happens in the component
      })
      .addCase(initiateIntegration.rejected, (state, action: any) => {
        state.initiating = false;
        state.error = action.payload ?? "Impossibile avviare la connessione";
      });

    // --- disconnectIntegration ---
    builder
      .addCase(disconnectIntegration.pending, (state) => {
        state.disconnecting = true;
        state.error = null;
      })
      .addCase(disconnectIntegration.fulfilled, (state) => {
        state.disconnecting = false;
        state.connected = false;
        state.connectedAt = null;
        state.inventorySyncEnabled = false;
        state.lastSyncAt = null;
        state.syncResult = null;
      })
      .addCase(disconnectIntegration.fulfilled, (state) => {
        state.amazonTokenRevoked = false;
        state.reauthUrl = null;
      })
      .addCase(disconnectIntegration.rejected, (state, action: any) => {
        state.disconnecting = false;
        state.error = action.payload ?? "Errore nella disconnessione";
      });

    // --- toggleInventorySync ---
    builder
      .addCase(toggleInventorySync.pending, (state) => {
        state.togglingSync = true;
        state.error = null;
      })
      .addCase(toggleInventorySync.fulfilled, (state, action: any) => {
        state.togglingSync = false;
        state.inventorySyncEnabled = action.payload?.inventorySyncEnabled ?? state.inventorySyncEnabled;
      })
      .addCase(toggleInventorySync.rejected, (state, action: any) => {
        state.togglingSync = false;
        state.error = action.payload ?? "Errore nell'aggiornamento della sincronizzazione";
      });

    // --- syncInventoryNow ---
    builder
      .addCase(syncInventoryNow.pending, (state) => {
        state.syncing = true;
        state.syncResult = null;
        state.error = null;
      })
      .addCase(syncInventoryNow.fulfilled, (state, action: any) => {
        state.syncing = false;
        state.lastSyncAt = action.payload?.lastSyncAt ?? new Date().toISOString();
        state.syncResult = {
          synced: action.payload?.synced ?? 0,
          errors: action.payload?.errors ?? 0,
          total: action.payload?.total ?? 0,
        };
      })
      .addCase(syncInventoryNow.rejected, (state, action: any) => {
        state.syncing = false;
        state.error = action.payload ?? "Errore durante la sincronizzazione";
      });
  },
});

export const { clearIntegrationError, clearSyncResult } = integrationSlice.actions;
export const integrationReducer = integrationSlice.reducer;
